import { useState, type KeyboardEvent } from "react";
import type { ForecastModel, ForecastStage, ForecastBand } from "@excellent/core";
import { Card, CardContent } from "../../card";
import { SectionTitle } from "../section-title";
import { MetricChart } from "../metric/metric-chart";
import { paceVerdict, type PaceVerdict } from "../metric/types";
import { Icon } from "../../icon";
import { Link } from "../link";
import { formatMoneyCompact } from "./format";
import { cn } from "../../utils";

/**
 * ForecastView — the live Customers ▸ Forecast instrument (the wired-in form of
 * the /styleguide/customers-forecast prototype; docs/design/CUSTOMERS-FORECAST-
 * REDESIGN.md). Replaces the two stacked, partly-redundant backward-looking
 * tables (PortfolioView forecast + ReportsView) with one forward-looking read:
 *
 *   1. a generated read (templated from real numbers — phrases light bars + open chat)
 *   2. the forecast hero — booked vs target vs pace ramp → "$Xk behind"
 *   3. the funnel — banded stage bars (weighted value × stall health)
 *   4. conversion + activity (the reports, reframed + de-duplicated)
 *
 * Fed entirely by `forecastModel()` (server) so it stays presentational. All
 * money arrives as minor units and renders through the shared formatters.
 */

const BAND_BG: Record<ForecastBand, string> = {
  ok: "bg-success",
  watch: "bg-warning",
  risk: "bg-danger",
};

export interface ForecastViewProps {
  model: ForecastModel;
  /** Where "Set a quarter target" points (CRM settings). */
  settingsHref?: string;
}

export function ForecastView({ model, settingsHref = "/settings/customers/pipelines" }: ForecastViewProps) {
  // The focus token shared between the read and the surfaces it describes: a
  // stage key, "trajectory", or null. Hovering a read phrase OR a bar sets it.
  const [focus, setFocus] = useState<string | null>(null);

  if (model.empty) {
    return (
      <Card className="gap-0 overflow-hidden py-0">
        <SectionTitle variant="card" title="Revenue forecast" />
        <CardContent className="flex flex-col items-center gap-2 px-6 py-12 text-center">
          <Icon name="trending-up" className="text-muted-foreground" />
          <p className="type-body text-muted-foreground">
            No pipeline to forecast yet — add deals to the pipeline and they&rsquo;ll roll up here as a
            booked-vs-target trajectory.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ForecastRead model={model} focus={focus} setFocus={setFocus} settingsHref={settingsHref} />
      <ForecastHero model={model} focused={focus === "trajectory"} settingsHref={settingsHref} />
      <FunnelPulse model={model} focus={focus} setFocus={setFocus} />
      <div className="grid gap-4 md:grid-cols-2">
        <ConversionCard model={model} />
        <ActivityCard model={model} />
      </div>
    </div>
  );
}

// ── 1 · the generated read ─────────────────────────────────────────────────────

/**
 * Open the bottom dock chat seeded with `query` — the DockBar catches
 * `excellent:ask-agent`, prefills + focuses (the app's single chat surface). The
 * same seam every PulseSynthesis read uses, applied to the forecast read.
 */
function chatAbout(query: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("excellent:ask-agent", { detail: { query } }));
}

function ForecastRead({
  model,
  focus,
  setFocus,
  settingsHref,
}: {
  model: ForecastModel;
  focus: string | null;
  setFocus: (v: string | null) => void;
  settingsHref: string;
}) {
  const usdK = (m: number) => formatMoneyCompact(m, model.currency);

  // The seeded questions a phrase / the affordance opens in the dock chat —
  // derived from the real numbers so the chat starts grounded in the forecast.
  const gap = model.gapMinor ?? 0;
  const pageQuestion =
    model.targetMinor == null
      ? `Our pipeline projects ${usdK(model.projectedMinor)} closeable this quarter. What's the most important thing to do to convert it?`
      : gap > 0
        ? `We're forecast to close ${usdK(model.projectedMinor)} this quarter — ${usdK(gap)} short of the ${usdK(model.targetMinor)} target with ${model.quarter.daysLeft} days left. What's the most important thing to do to close the gap?`
        : `We're forecast to close ${usdK(model.projectedMinor)}, clearing the ${usdK(model.targetMinor)} target. What should I do to lock it in?`;
  const stage = model.stages.find((s) => s.key === model.headlineStageKey) ?? null;
  const stageQuestion = stage
    ? model.headlineIsDrag
      ? `The ${stage.count} ${stage.name} deals (${usdK(stage.weightedMinor)} weighted) are stalled past their expected close. Why, and how do I unstick them?`
      : `${stage.name} holds the most weighted value (${usdK(stage.weightedMinor)} across ${stage.count} deals). How do I close it to hit the forecast?`
    : pageQuestion;

  // A read phrase: hover (or focus) lights its surface — the hero or its funnel
  // bar — and click (or Enter) opens the dock chat seeded with `q`. The
  // PulseSynthesis interaction, applied to the deterministic forecast read.
  const hot = (k: string, q: string) => ({
    role: "button" as const,
    tabIndex: 0,
    onMouseEnter: () => setFocus(k),
    onMouseLeave: () => setFocus(null),
    onFocus: () => setFocus(k),
    onBlur: () => setFocus(null),
    onClick: () => chatAbout(q),
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        chatAbout(q);
      }
    },
    className: cn(
      "cursor-pointer rounded-sm px-0.5 text-foreground underline decoration-dotted decoration-muted-foreground/40 underline-offset-4 outline-none transition-colors hover:decoration-primary/60 focus-visible:bg-primary/10",
      focus === k && "bg-primary/10 decoration-primary/60",
    ),
  });

  const stageClause = stage ? (
    model.headlineIsDrag ? (
      <>
        {" "}
        The drag is <span {...hot(stage.key, stageQuestion)}>{stage.name}</span>:{" "}
        <span {...hot(stage.key, stageQuestion)}>{stage.count} deals</span> worth{" "}
        <span {...hot(stage.key, stageQuestion)}>{usdK(stage.weightedMinor)}</span> have stalled past
        their expected close.
      </>
    ) : (
      <>
        {" "}
        The lever is <span {...hot(stage.key, stageQuestion)}>{stage.name}</span>:{" "}
        <span {...hot(stage.key, stageQuestion)}>{usdK(stage.weightedMinor)}</span> weighted across{" "}
        <span {...hot(stage.key, stageQuestion)}>{stage.count} deals</span> — closing it books the most.
      </>
    )
  ) : null;

  return (
    <div className="group/synthesis relative">
      <p className="type-heading font-medium tracking-[-0.01em] text-muted-foreground">
        {model.targetMinor == null ? (
          <>
            Your pipeline projects{" "}
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.projectedMinor)}</span> in closeable
            revenue this quarter (
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.bookedMinor)}</span> booked +{" "}
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.weightedOpenMinor)}</span> weighted),
            with {model.quarter.daysLeft} days left.{stageClause}{" "}
            <Link href={settingsHref} variant="inline">
              Set a quarter target
            </Link>{" "}
            to track pace.
          </>
        ) : model.gapMinor != null && model.gapMinor > 0 ? (
          <>
            You&rsquo;re tracking to close{" "}
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.projectedMinor)}</span> this quarter —{" "}
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.gapMinor)} short</span> of the{" "}
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.targetMinor)} target</span>, with{" "}
            {model.quarter.daysLeft} days left.{stageClause} You&rsquo;re projected to land near{" "}
            <span {...hot("trajectory", pageQuestion)}>{model.landPct}%</span>.
          </>
        ) : (
          <>
            You&rsquo;re tracking to close{" "}
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.projectedMinor)}</span> this quarter —
            clearing the{" "}
            <span {...hot("trajectory", pageQuestion)}>{usdK(model.targetMinor)} target</span> with{" "}
            {model.quarter.daysLeft} days left.{stageClause}
          </>
        )}
      </p>
      <button
        type="button"
        aria-label="Chat about this forecast"
        onClick={() => chatAbout(pageQuestion)}
        className="absolute left-full top-1 z-30 ml-3 flex items-center gap-1.5 rounded-md px-1.5 py-1 text-muted-foreground/40 outline-none transition-colors hover:text-foreground focus-visible:text-foreground group-hover/synthesis:text-muted-foreground"
      >
        <Icon name="message-square" size="sm" />
        <span className="type-subtext opacity-0 transition-opacity group-hover/synthesis:opacity-100">Ask</span>
      </button>
    </div>
  );
}

// ── 2 · forecast hero ──────────────────────────────────────────────────────────

function ForecastHero({
  model,
  focused,
  settingsHref,
}: {
  model: ForecastModel;
  focused: boolean;
  settingsHref: string;
}) {
  const usdK = (m: number) => formatMoneyCompact(m, model.currency);
  const hasTarget = model.targetMinor != null;
  const gap = model.gapMinor ?? 0;
  // The pace verdict for the hero trend slot + the chart end-dot.
  const verdict =
    model.pace === "none"
      ? paceVerdict("pending")
      : paceVerdict(model.pace as PaceVerdict);
  const trend = hasTarget
    ? {
        label: gap > 0 ? `${usdK(gap)} behind target` : "On track",
        intent: gap > 0 ? (model.pace === "at-risk" ? ("danger" as const) : ("warning" as const)) : ("success" as const),
      }
    : undefined;

  return (
    <Card
      className={cn(
        "gap-0 overflow-hidden py-0 transition-shadow",
        focused && "ring-2 ring-primary/40",
      )}
    >
      <CardContent className="p-0">
        <SectionTitle variant="card" title="Revenue forecast" trend={trend} />
        <div className="grid md:grid-cols-[minmax(0,256px)_1fr]">
          {/* the number, decomposed — bounded off from the graph by a full-height divider */}
          <div className="flex flex-col justify-center gap-4 px-6 py-5 md:border-r md:border-border">
            <div>
              <div className="type-heading font-medium tabular-nums text-foreground">
                {usdK(model.projectedMinor)}
              </div>
              <div className="mt-1 type-body text-muted-foreground">
                projected close · {model.quarter.daysLeft} days left
              </div>
            </div>
            <div className="space-y-2 border-t border-border/40 pt-3">
              <Reading label="Booked (QTD)" value={usdK(model.bookedMinor)} tone="ok" />
              <Reading label="Weighted open" value={usdK(model.weightedOpenMinor)} tone="info" />
              {hasTarget ? (
                <Reading
                  label="Gap to target"
                  value={gap > 0 ? `−${usdK(gap)}` : `+${usdK(-gap)}`}
                  tone={gap > 0 ? "risk" : "ok"}
                />
              ) : (
                <div className="flex items-center justify-between type-body">
                  <span className="text-muted-foreground">Quarter target</span>
                  <Link href={settingsHref} variant="inline" className="type-subtext">
                    Set target
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* the trajectory: booked line vs target line vs pace ramp */}
          <div className="flex flex-col justify-center gap-3 px-6 py-5">
            <MetricChart
              history={model.bookedSeries}
              reference={
                hasTarget
                  ? [
                      { mode: "flat-target", value: model.targetMinor!, label: `target ${usdK(model.targetMinor!)}` },
                      { mode: "pace-ramp", start: 0, target: model.targetMinor!, elapsed: model.quarter.elapsed },
                    ]
                  : { mode: "none" }
              }
              verdict={verdict}
              domain={
                hasTarget
                  ? { min: 0, max: Math.max(model.targetMinor!, ...model.bookedSeries) }
                  : "fit"
              }
              className="h-48 w-full"
              ariaLabel="Booked bookings versus quarter target and pace"
            />
            <div className="flex items-center justify-between type-subtext text-muted-foreground">
              <span>wk 1</span>
              <span>
                now · day {Math.min(model.quarter.days, model.quarter.dayOf + 1)}/
                {model.quarter.days}
              </span>
              <span>quarter end</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/40 pt-3 type-subtext text-muted-foreground">
              <LegendItem kind="solid" color="var(--chart-1)" label="Booked" />
              {hasTarget ? (
                <>
                  <LegendItem kind="dashed" color="var(--primary)" label="Pace ramp" />
                  <LegendItem kind="dashed" color="var(--muted-foreground)" label="Target" />
                </>
              ) : null}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Reading({ label, value, tone }: { label: string; value: string; tone: "ok" | "info" | "risk" }) {
  const color =
    tone === "ok" ? "text-success" : tone === "risk" ? "text-danger" : "text-foreground";
  return (
    <div className="flex items-center justify-between type-body">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("tabular-nums", color)}>{value}</span>
    </div>
  );
}

function LegendItem({ kind, color, label }: { kind: "solid" | "dashed"; color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        aria-hidden
        className={cn("inline-block w-4", kind === "solid" ? "h-[3px] rounded-full" : "border-t border-dashed")}
        style={kind === "solid" ? { backgroundColor: color } : { borderColor: color }}
      />
      {label}
    </span>
  );
}

// ── 3 · funnel pulse ───────────────────────────────────────────────────────────

function FunnelPulse({
  model,
  focus,
  setFocus,
}: {
  model: ForecastModel;
  focus: string | null;
  setFocus: (v: string | null) => void;
}) {
  const TRACK = 132; // px
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="p-0">
        <SectionTitle
          variant="card"
          title="Pipeline funnel"
          subtitle="weighted value by stage — a tall red bar is value that's stalling"
        />
        <div className="flex items-end gap-3 px-6 pb-3 pt-5 sm:gap-5">
          {model.stages.map((s) => (
            <FunnelBar
              key={s.key}
              stage={s}
              track={TRACK}
              active={focus === s.key}
              setFocus={setFocus}
              currency={model.currency}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelBar({
  stage,
  track,
  active,
  setFocus,
  currency,
}: {
  stage: ForecastStage;
  track: number;
  active: boolean;
  setFocus: (v: string | null) => void;
  currency: string;
}) {
  return (
    <Link
      href={`/customers?view=list&stage=${stage.key}`}
      variant="unstyled"
      onMouseEnter={() => setFocus(stage.key)}
      onMouseLeave={() => setFocus(null)}
      aria-label={`Open ${stage.name} deals`}
      className="group flex flex-1 flex-col items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <div className="relative flex w-full items-end" style={{ height: track }}>
        <span className="absolute inset-0 rounded-xs bg-muted/25" />
        <span
          className={cn(
            "relative z-20 w-full rounded-xs transition-all group-hover:brightness-125",
            BAND_BG[stage.band],
            active && "ring-2 ring-primary/60 brightness-125",
          )}
          style={{ height: `${stage.heightPct}%` }}
        />
      </div>
      <div className="mt-2 w-full text-center">
        <div className="truncate type-subtext font-medium text-foreground">{stage.name}</div>
        <div className="type-subtext tabular-nums text-muted-foreground">
          {stage.count === 0
            ? "—"
            : `${stage.count} · ${formatMoneyCompact(stage.weightedMinor, currency)}`}
        </div>
      </div>
    </Link>
  );
}

// ── 4 · supporting detail ──────────────────────────────────────────────────────

function ConversionCard({ model }: { model: ForecastModel }) {
  const usdK = (m: number) => formatMoneyCompact(m, model.currency);
  const c = model.conversion;
  const items: { label: string; value: string }[] = [
    { label: "Lead → Opp", value: `${c.leadToOppPct}%` },
    { label: "Opp → Won", value: `${c.oppToWonPct}%` },
    { label: "Win rate", value: c.winRatePct == null ? "—" : `${c.winRatePct}%` },
    { label: "Avg deal", value: c.avgDealMinor == null ? "—" : usdK(c.avgDealMinor) },
    { label: "Sales cycle", value: c.cycleDays == null ? "—" : `${c.cycleDays}d` },
  ];
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="p-0">
        <SectionTitle variant="card" title="Conversion" subtitle="funnel rates this quarter" />
        <div className="grid grid-cols-3 gap-x-4 gap-y-4 px-6 py-5">
          {items.map((f) => (
            // Label-led, matching StatCard's one metric ordering (muted label
            // above the tabular value) — kept at type-heading for this compact
            // supporting-detail card rather than promoted to a StatStrip band.
            <div key={f.label} className="flex flex-col gap-1">
              <div className="type-subtext text-muted-foreground">{f.label}</div>
              <div className="type-heading font-medium tabular-nums text-foreground">{f.value}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ActivityCard({ model }: { model: ForecastModel }) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="p-0">
        <SectionTitle variant="card" title="Activity by owner" subtitle="touches logged this quarter" />
        {model.activity.length === 0 ? (
          <p className="px-6 py-6 type-body text-muted-foreground">No activity logged yet.</p>
        ) : (
          <div className="px-6 py-2">
            {model.activity.map((a) => (
              <div
                key={a.owner}
                className="flex items-center justify-between border-b border-border/40 py-3 first:pt-2 last:border-b-0"
              >
                <span className="type-body text-foreground">{a.owner}</span>
                <span className="flex items-center gap-3 type-subtext tabular-nums text-muted-foreground">
                  <span>{a.calls} calls</span>
                  <span>{a.emails} emails</span>
                  <span>{a.sms} sms</span>
                  <span>{a.meetings} mtgs</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
